import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import WarehouseOutlinedIcon from "@mui/icons-material/WarehouseOutlined";
import { SectionCard } from "./SectionCard";
import { SettingsSection } from "./SettingsSection";
import type { UseMutationResult } from "@tanstack/react-query";

interface DepotsTabProps {
  effectiveClientId: string | null;
  createDepotMutation: UseMutationResult<
    unknown,
    unknown,
    { name: string; is_active: boolean }
  >;
  updateDepotMutation: UseMutationResult<
    unknown,
    unknown,
    { depotId: string; payload: { name?: string; is_active?: boolean } }
  >;
  deleteDepotMutation: UseMutationResult<unknown, unknown, string>;
  depotsLoading: boolean;
  depotsList: { id: string; name: string; is_active?: boolean }[];
}

export const DepotsTab: React.FC<DepotsTabProps> = ({
  effectiveClientId,
  createDepotMutation,
  updateDepotMutation,
  deleteDepotMutation,
  depotsLoading,
  depotsList,
}) => {
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState("");
  const [addActive, setAddActive] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editActive, setEditActive] = useState(true);

  const filtered = depotsList.filter((d) =>
    d.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleOpenAdd = () => {
    setAddName("");
    setAddActive(true);
    setAddOpen(true);
  };

  const handleConfirmAdd = () => {
    if (!addName.trim() || !effectiveClientId) return;
    createDepotMutation.mutate(
      { name: addName.trim(), is_active: addActive },
      { onSuccess: () => setAddOpen(false) }
    );
  };

  const handleStartEdit = (depot: {
    id: string;
    name: string;
    is_active?: boolean;
  }) => {
    setEditingId(depot.id);
    setEditName(depot.name);
    setEditActive(depot.is_active !== false);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName("");
  };

  const handleSaveEdit = (id: string) => {
    updateDepotMutation.mutate(
      { depotId: id, payload: { name: editName, is_active: editActive } },
      { onSuccess: () => handleCancelEdit() }
    );
  };

  return (
    <SettingsSection>
      {!effectiveClientId && (
        <Alert severity="info">Select a client to manage depots.</Alert>
      )}

      <SectionCard
        title="Depots"
        description={`${depotsList.length} depot${depotsList.length === 1 ? "" : "s"} configured`}
        action={
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            disabled={!effectiveClientId}
            onClick={handleOpenAdd}
          >
            Add depot
          </Button>
        }
      >
        <TextField
          size="small"
          fullWidth
          placeholder="Search depots…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{ mb: 2 }}
        />

        <Divider />

        {depotsLoading ? (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Loading depots…
            </Typography>
          </Stack>
        ) : depotsList.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
            <WarehouseOutlinedIcon sx={{ fontSize: 36, opacity: 0.3, mb: 1 }} />
            <Typography variant="body2">No depots yet.</Typography>
            <Typography variant="caption">
              Click "Add depot" to create one.
            </Typography>
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ py: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No depots match "{search}"
            </Typography>
          </Box>
        ) : (
          <Stack divider={<Divider />} spacing={0}>
            {filtered.map((depot) => {
              const isEditing = editingId === depot.id;
              const isActive = depot.is_active !== false;

              return (
                <Box key={depot.id} sx={{ py: 1.25 }}>
                  {isEditing ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        size="small"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && editName.trim())
                            handleSaveEdit(depot.id);
                          if (e.key === "Escape") handleCancelEdit();
                        }}
                        sx={{ flex: 1 }}
                      />
                      <Chip
                        label={editActive ? "Active" : "Inactive"}
                        size="small"
                        color={editActive ? "success" : "default"}
                        variant={editActive ? "filled" : "outlined"}
                        onClick={() => setEditActive((v) => !v)}
                        sx={{ cursor: "pointer" }}
                      />
                      <Tooltip title="Save">
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            disabled={
                              !editName.trim() || updateDepotMutation.isPending
                            }
                            onClick={() => handleSaveEdit(depot.id)}
                          >
                            {updateDepotMutation.isPending ? (
                              <CircularProgress size={16} />
                            ) : (
                              <CheckIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Cancel">
                        <IconButton size="small" onClick={handleCancelEdit}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ) : (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {depot.name}
                      </Typography>
                      <Chip
                        label={isActive ? "Active" : "Inactive"}
                        size="small"
                        color={isActive ? "success" : "default"}
                        variant="outlined"
                      />
                      <Tooltip title="Rename">
                        <IconButton
                          size="small"
                          onClick={() => handleStartEdit(depot)}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          disabled={deleteDepotMutation.isPending}
                          onClick={() => {
                            if (
                              window.confirm(
                                "Delete this depot? This cannot be undone."
                              )
                            ) {
                              deleteDepotMutation.mutate(depot.id);
                            }
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </SectionCard>

      <Dialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add depot</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              size="small"
              fullWidth
              label="Depot name"
              value={addName}
              onChange={(e) => setAddName(e.target.value)}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter" && addName.trim()) handleConfirmAdd();
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={addActive}
                  onChange={(e) => setAddActive(e.target.checked)}
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!addName.trim() || createDepotMutation.isPending}
            onClick={handleConfirmAdd}
          >
            {createDepotMutation.isPending ? (
              <CircularProgress size={18} />
            ) : (
              "Add depot"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </SettingsSection>
  );
};
